// @ts-strict-ignore
import { useState } from "react";

import { useStoreCurrentSetupPageMutation } from "@shared/data/mutations/StoreCurrentSetupPageMutation/StoreCurrentSetupPage.mutation";
import { CanadaOAuthOnboardingFlow } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/CanadaOAuthOnboardingFlow";
import { OAuthAccountTypeStep } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthAccountTypeStep/OAuthAccountTypeStep";
import { OAuthAuthorizationStep } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthAuthorizationStep/OAuthAuthorizationStep";
import { OAuthBankingStep } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthBankingStep/OAuthBankingStep";
import { OAuthCertificationStep } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthCertificationStep/OAuthCertificationStep";
import { OAuthLandingStep } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthLandingStep/OAuthLandingStep";
import { OAuthOnboardingWrapper } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthOnboardingWrapper/OAuthOnboardingWrapper";
import { OAuthSignInStep } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthSignInStep/OAuthSignInStep";
import { OAuthSignUpStep } from "@unauthenticated/oauth/components/OAuthOnboardingFlow/OAuthSignUpStep/OAuthSignUpStep";

import { OAuthExternalApplicationStep } from "./OAuthExternalApplicationStep/OAuthExternalApplicationStep";
import { OAuthStep } from "./oauthStep";
import { EXTERNAL_APPLICATION_STRING } from "./types";

interface Props {
  applicationName: string;
  applicationLogo: string;
  clientId: string;
  redirectUri: string;
  responseType: string;
  scopes: string;
  state: string;
  applicationAccessLevel: string;
  ssoErrors?: string;
  ssoOrigin: string;
  currentCountry: string;
  nonce: string;
  prompt: string;
  maxAge: string;
}

type OauthStoreCurrentSetupPage = "student_identification" | "certificate" | "complete";

const OAuthOnboardingFlow = ({ currentCountry, ...props }: Props) => {
  const {
    applicationLogo,
    clientId,
    redirectUri,
    responseType,
    scopes,
    state,
    applicationAccessLevel,
    ssoErrors,
    ssoOrigin,
    nonce,
    prompt,
    maxAge,
  } = props;

  const [ssoOriginState, setSsoOriginState] = useState(ssoOrigin);
  const [ssoErrorMessage, setSsoErrorMessage] = useState(ssoErrors);
  const isExternalApp = applicationAccessLevel === EXTERNAL_APPLICATION_STRING;
  const isOIDCAuth = () => scopes.includes("openid");
  const determineInitialStep: () => OAuthStep = () => {
    if (isExternalApp) {
      return OAuthStep.EXTERNAL_APPLICATION;
    } else if (isOIDCAuth() && !ssoOrigin) {
      // !ssoOrigin ensures sign in screen
      // is not shown after SSO redirect
      return OAuthStep.SIGN_IN;
    } else {
      return OAuthStep.LANDING_PAGE;
    }
  };

  const [currentStep, setCurrentStep] = useState<OAuthStep>(determineInitialStep());
  const [isFromSignIn, setIsFromSignIn] = useState(false);
  const [updateCurrentSetupPage] = useStoreCurrentSetupPageMutation();
  const target = history.state?.target;

  const handleSignInComplete = () => {
    if (isExternalApp) {
      setCurrentStep(OAuthStep.EXTERNAL_APPLICATION);
    } else {
      setCurrentStep(OAuthStep.AUTHORIZATION);
      setIsFromSignIn(true);
    }
  };

  const handleOnboardingComplete = () => {
    if (isExternalApp) {
      setSsoOriginState(null);
      setCurrentStep(OAuthStep.EXTERNAL_APPLICATION);
    } else {
      setCurrentStep(OAuthStep.AUTHORIZATION);
    }
  };

  const updateStoreCurrentSetupPage = async (
    step: OauthStoreCurrentSetupPage,
    forProfit?: boolean
  ) => {
    await updateCurrentSetupPage({
      variables: {
        input: {
          attributes: {
            skipToStep: step,
            apiClientId: clientId,
            forProfit: forProfit || false,
          },
        },
      },
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case OAuthStep.SIGN_IN:
        return (
          <OAuthSignInStep
            onSignInComplete={handleSignInComplete}
            ssoErrors={ssoErrorMessage}
            setSsoErrorMessage={setSsoErrorMessage}
            onUpdateCurrentStep={setCurrentStep}
            isOIDCAuth={isOIDCAuth()}
          />
        );
      case OAuthStep.SIGN_UP:
        return (
          <OAuthSignUpStep
            setSsoErrorMessage={setSsoErrorMessage}
            clientId={clientId}
            onUpdateCurrentStep={setCurrentStep}
            ssoErrors={ssoErrorMessage}
          />
        );
      case OAuthStep.EXTERNAL_APPLICATION:
        return (
          <OAuthExternalApplicationStep
            target={target}
            ssoOrigin={ssoOriginState}
            onUpdateCurrentStep={setCurrentStep}
            redirectUri={redirectUri}
          />
        );
      case OAuthStep.CERTIFICATION:
        return (
          <OAuthCertificationStep
            onOnboardingComplete={handleOnboardingComplete}
            onUpdateCurrentStep={setCurrentStep}
            onUpdateStoreCurrentSetupPage={updateStoreCurrentSetupPage}
          />
        );
      case OAuthStep.ACCOUNT_TYPE:
        return (
          <OAuthAccountTypeStep
            onUpdateCurrentStep={setCurrentStep}
            onOnboardingComplete={handleOnboardingComplete}
            onUpdateStoreCurrentSetupPage={updateStoreCurrentSetupPage}
          />
        );
      case OAuthStep.PAYMENT:
        return (
          <OAuthBankingStep
            onUpdateCurrentStep={setCurrentStep}
            onOnboardingComplete={handleOnboardingComplete}
            onUpdateStoreCurrentSetupPage={updateStoreCurrentSetupPage}
          />
        );
      case OAuthStep.AUTHORIZATION:
        return (
          <OAuthAuthorizationStep
            clientId={clientId}
            redirectUri={redirectUri}
            responseType={responseType}
            scopes={scopes}
            state={state}
            isFromSignIn={isFromSignIn}
            nonce={nonce}
            prompt={prompt}
            maxAge={maxAge}
            skipConsent={isOIDCAuth()}
          />
        );
      case OAuthStep.LANDING_PAGE:
        return (
          <OAuthLandingStep
            target={target}
            ssoOrigin={ssoOriginState}
            onUpdateCurrentStep={setCurrentStep}
          />
        );
    }
  };

  if (currentCountry === "Canada") return <CanadaOAuthOnboardingFlow {...props} />;

  return (
    <div data-testid="us-oauth">
      <OAuthOnboardingWrapper
        onUpdateCurrentStep={setCurrentStep}
        currentStep={currentStep}
        applicationLogo={applicationLogo}
      >
        {renderCurrentStep()}
      </OAuthOnboardingWrapper>
    </div>
  );
};

export { OAuthOnboardingFlow };
